import type { WatchQueryFetchPolicy } from "@apollo/client";
import {
  ColumnDef,
  ColumnPinningState,
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Text } from "@chakra-ui/react";

import { DocumentType, getFragmentData, gql } from "__generated__/gql";
import {
  useChakraReactTableProps,
  usePrepApolloData,
} from "components/Shared/ChakraReactTable/utils";
import { useAdminOrBrandContext } from "utils/apollo/hooks/adminOrBrand";
import usePagination, {
  usePaginationReset,
} from "components/Shared/ChakraReactTable/usePagination";
import useGQLSorting, {
  GQLOrderByEnumsById,
} from "components/Shared/ChakraReactTable/useGQLSorting";
import {
  formatIntegerCell,
  formatJustifiedDateTimeCell,
} from "components/Shared/ChakraReactTable/formatters";
import useStickyHeader from "components/Shared/ChakraReactTable/useStickyHeader";
import { NameWithFlagIcons } from "components/Shared/RetailerDirectory/utils";
import {
  RetailerLocationGroupFilter,
  RetailerLocationGroupsOrderBy,
} from "__generated__/gql/graphql";
import useFixedLayout from "components/Shared/ChakraReactTable/useFixedLayout";
import useSelectableRow from "components/Shared/ChakraReactTable/useSelectableRow";
import { useQuery } from "utils/apollo/hooks";
import FullPageTable, {
  FullPageTableProps,
} from "components/Shared/ChakraReactTable/FullPageTable";
import RetailerTypeBadge from "components/Shared/RetailerDirectory/RetailerTypeBadge";
import LocationGroupName from "components/Shared/RetailerDirectory/LocationGroups/LocationGroupName";

export const fragment = gql(/* GraphQL */ `
  fragment RetailerLocationGroupsTableRow on RetailerLocationGroup {
    id
    name
    createdAt
    retailer {
      id
      nameWithUniqueSuffix
      retailerType
      ...RetailerNameWithFlagIconsFragment
    }
    retailerLocationGroupRetailerLocations {
      totalCount
    }
    ...RetailerLocationGroupNameFragment
  }
`);

const query = gql(/* GraphQL */ `
  query RetailerLocationGroupsTable(
    $filter: RetailerLocationGroupFilter
    $orderBy: [RetailerLocationGroupsOrderBy!] = [NAME_ASC]
    $limit: Int = 10
    $offset: Int = 0
  ) {
    retailerLocationGroups(
      filter: $filter
      first: $limit
      offset: $offset
      orderBy: $orderBy
    ) {
      totalCount
      nodes {
        id
        ...RetailerLocationGroupsTableRow
      }
    }
  }
`);

export type Datum = DocumentType<typeof fragment>;

const col = createColumnHelper<Datum>();

export const RETAILER_COLUMNS = [
  col.accessor((r) => r.retailer?.nameWithUniqueSuffix, {
    id: "retailerName",
    header: "Retailer",
    cell: (cell) => <NameWithFlagIcons retailer={cell.row.original.retailer} />,
    size: 250,
  }),
  col.accessor((r) => r.retailer?.retailerType, {
    id: "retailerType",
    header: "Retailer Type",
    cell: (cell) => {
      const retailerType = cell.getValue();
      if (!retailerType) return null;
      return (
        <RetailerTypeBadge
          retailerType={retailerType}
          isTruncated
          maxW="100%"
          fontSize=".8em"
          lineHeight={1.5}
        />
      );
    },
    size: 120,
    meta: { props: { textAlign: "center" }, cellProps: { isTruncated: true } },
  }),
];

export const LOCATION_GROUP_COLUMNS = [
  col.accessor("name", {
    header: "Name",
    cell: (cell) => <LocationGroupName location={cell.row.original} />,
    size: 175,
  }),
  col.accessor((r) => r.retailerLocationGroupRetailerLocations.totalCount, {
    id: "numLocations",
    header: "# of Locations",
    cell: formatIntegerCell,
    size: 125,
    meta: {
      headerProps: { textAlign: "center" },
      cellProps: { isNumeric: true, pr: "7.25%" },
    },
  }),
  col.accessor("createdAt", {
    header: "Added",
    cell: (cell) => formatJustifiedDateTimeCell(cell),
    meta: {
      props: { textAlign: "right" },
      cellProps: { fontSize: ".85em", lineHeight: "normal" },
    },
    size: 115,
  }),
];

export const DEFAULT_COLUMNS = [
  ...LOCATION_GROUP_COLUMNS.slice(0, -1),
  ...RETAILER_COLUMNS,
  ...LOCATION_GROUP_COLUMNS.slice(-1),
];

export const DEFAULT_PINNED_COLUMNS: ColumnPinningState = { left: ["name"] };

export const orderByEnumsById: GQLOrderByEnumsById<RetailerLocationGroupsOrderBy> =
  {
    name: [
      RetailerLocationGroupsOrderBy.NAME_ASC,
      RetailerLocationGroupsOrderBy.NAME_DESC,
    ],
    numLocations: [
      RetailerLocationGroupsOrderBy.RETAILER_LOCATION_GROUP_RETAILER_LOCATIONS_COUNT_ASC,
      RetailerLocationGroupsOrderBy.RETAILER_LOCATION_GROUP_RETAILER_LOCATIONS_COUNT_DESC,
    ],
    retailerName: [
      RetailerLocationGroupsOrderBy.RETAILER__NAME_ASC,
      RetailerLocationGroupsOrderBy.RETAILER__NAME_DESC,
    ],
    retailerType: [
      RetailerLocationGroupsOrderBy.RETAILER__RETAILER_TYPE_ASC,
      RetailerLocationGroupsOrderBy.RETAILER__RETAILER_TYPE_DESC,
    ],
    createdAt: [
      RetailerLocationGroupsOrderBy.CREATED_AT_ASC,
      RetailerLocationGroupsOrderBy.CREATED_AT_DESC,
    ],
  };

export type RetailerLocationGroupsTableProps = Pick<
  FullPageTableProps<Datum>,
  "scrollContainerProps" | "tableContainerProps" | "tableControlsFooterProps"
> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns?: ColumnDef<Datum, any>[];
  defaultPageSize?: number;
  filter?: RetailerLocationGroupFilter;
  isAdmin?: boolean;
  isReady?: boolean;
  nextFetchPolicy?: WatchQueryFetchPolicy;
  onSelectedChange?: (id: UUID) => void;
  pinnedColumns?: ColumnPinningState;
  selected?: UUID | null;
};

export default function RetailerLocationGroupsTable({
  columns = DEFAULT_COLUMNS,
  defaultPageSize = 10,
  filter,
  isAdmin,
  isReady = true,
  nextFetchPolicy,
  onSelectedChange,
  pinnedColumns: columnPinning = DEFAULT_PINNED_COLUMNS,
  selected,
  scrollContainerProps,
  tableContainerProps,
  tableControlsFooterProps,
}: RetailerLocationGroupsTableProps) {
  const {
    pagination,
    onPaginationChange,
    limitOffset,
    getPageCount,
    setPageIndex,
  } = usePagination({ pageSize: defaultPageSize });

  const { sorting, onSortingChange, orderBy } = useGQLSorting(
    orderByEnumsById,
    [{ id: "createdAt", desc: false }],
  );

  const isPendingFilterReset = usePaginationReset(
    filter,
    setPageIndex,
    pagination.pageIndex,
  );

  const isPendingSortingReset = usePaginationReset(
    sorting,
    setPageIndex,
    pagination.pageIndex,
  );

  const [context, isContextReady] = useAdminOrBrandContext(isAdmin);

  const {
    data: rawData,
    loading: isLoading,
    refetch,
  } = useQuery(query, {
    context,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy,
    skip:
      !isReady ||
      !isContextReady ||
      isPendingFilterReset ||
      isPendingSortingReset,
    variables: { filter, orderBy, ...limitOffset },
  });

  const { nodes, totalCount } = rawData?.retailerLocationGroups || {};

  const tableInstance = useReactTable({
    columns,
    data: usePrepApolloData(getFragmentData(fragment, nodes)),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onPaginationChange,
    onSortingChange,
    manualPagination: true,
    manualSorting: true,
    enableSorting: true,
    state: { columnPinning, pagination, sorting },
    pageCount: getPageCount(totalCount),
  });

  const selectableRowProps = useSelectableRow<Datum>({
    selected,
    onSelectedChange: onSelectedChange ?? (() => {}),
  });

  const tableProps = useChakraReactTableProps<Datum>([
    useStickyHeader(),
    useFixedLayout(tableInstance),
    onSelectedChange ? selectableRowProps : {},
    {
      tableProps: { fontSize: "sm", variant: "condensed" },
      tableBodyProps: { sx: { "> tr > td": { py: 2 } } },
    },
  ]);

  return (
    <FullPageTable
      tableInstance={tableInstance}
      {...tableProps}
      {...{
        scrollContainerProps,
        tableContainerProps,
        tableControlsFooterProps,
      }}
      isLoading={isLoading || !isReady || typeof window === "undefined"}
      noRowsPlaceholder={<Text>No location groups found.</Text>}
      totalCount={totalCount}
      resultTypeLabel="location groups"
      onRefresh={refetch}
    />
  );
}
